import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { API } from '@/api'
import { TeamSnapshotWithUser } from '@/api/type-response'
import { ServerDomainInfo } from '@/api/type-server'
import { Snapshot } from '@/models/snapshot'

import { PAGE_SIZE } from '../constants'
import { getStatusCodes } from '../utils/statusFilters'

type SnapshotListType = 'team-member' | 'team-shared' | 'personal'

interface UseSnapshotListOptions {
  type: SnapshotListType
  teamResID?: string
  selectedMembers?: string[]
  selectedStatuses?: (string | number)[]
  enabled?: boolean
}

interface UseSnapshotListReturn<T> {
  snapshots: T[]
  loading: boolean
  page: number
  total: number
  error: Error | null
  fetchSnapshots: (
    newPage?: number,
    filterMembers?: string[],
    filterStatuses?: (string | number)[],
  ) => Promise<void>
  setPage: (page: number) => void
}

// Stable default values to avoid re-creating arrays on every render
const EMPTY_STRING_ARRAY: string[] = []
const DEFAULT_STATUSES: (string | number)[] = ['all']

export function useSnapshotList<T = TeamSnapshotWithUser>(
  options: UseSnapshotListOptions,
): UseSnapshotListReturn<T> {
  const { type, teamResID, enabled = true } = options

  // Use stable defaults to avoid reference changes
  const selectedMembers = options.selectedMembers ?? EMPTY_STRING_ARRAY
  const selectedStatuses = options.selectedStatuses ?? DEFAULT_STATUSES

  const [snapshots, setSnapshots] = useState<T[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [error, setError] = useState<Error | null>(null)
  const [domainInfos, setDomainInfos] = useState<ServerDomainInfo[]>([])

  // Fetch domains on mount
  const getDomains = useCallback(async () => {
    try {
      const res = await API.getDomainList()
      if (res.code === 0) {
        setDomainInfos(res.domainInfos || [])
      }
    } catch (error) {
      console.error('Failed to fetch domains:', error)
    }
  }, [])

  useEffect(() => {
    getDomains()
  }, [getDomains])

  // Convert domainInfos to domains array
  const domains = useMemo(() => domainInfos.map((d) => d.domain), [domainInfos])

  // Stabilize all array dependencies using JSON serialization
  const domainsKey = useMemo(() => JSON.stringify(domains), [domains])
  const membersKey = useMemo(() => JSON.stringify(selectedMembers), [selectedMembers])
  const statusesKey = useMemo(() => JSON.stringify(selectedStatuses), [selectedStatuses])

  // Store latest values in ref to avoid stale closures
  const filtersRef = useRef({ selectedMembers, selectedStatuses, domains })
  filtersRef.current = { selectedMembers, selectedStatuses, domains }

  // Stable fetch function - only depends on primitives and stable keys
  const fetchSnapshots = useCallback(
    async (newPage = 1, filterMembers?: string[], filterStatuses?: (string | number)[]) => {
      if (!enabled) return

      // Team snapshot APIs require teamResID
      if ((type === 'team-member' || type === 'team-shared') && !teamResID) {
        return
      }

      // Get latest values from ref
      const currentDomains = filtersRef.current.domains
      const members = filterMembers ?? filtersRef.current.selectedMembers
      const statuses = filterStatuses ?? filtersRef.current.selectedStatuses

      // All snapshot APIs require domains/regions
      if (currentDomains.length === 0) {
        return
      }

      setLoading(true)
      setError(null)

      try {
        const statusCodes = getStatusCodes(statuses)

        if (type === 'team-member') {
          const res = await API.getTeamMemberSnapshots({
            res_id: teamResID!,
            page: newPage,
            per_page: PAGE_SIZE,
            statuses: statusCodes,
            member_res_ids: members.length > 0 ? members : undefined,
            regions: currentDomains,
          })

          if (res.snapshots) {
            setSnapshots(res.snapshots as T[])
            setPage(newPage)
            setTotal(res.pagination?.total || 0)
          }
        } else if (type === 'team-shared') {
          const res = await API.getTeamSharedSnapshots({
            res_id: teamResID!,
            page: newPage,
            per_page: PAGE_SIZE,
            statuses: statusCodes,
            regions: currentDomains,
          })

          if (res.snapshots) {
            setSnapshots(res.snapshots as T[])
            setPage(newPage)
            setTotal(res.pagination?.total || 0)
          }
        } else if (type === 'personal') {
          const res = await API.getSnapList({
            domains: currentDomains,
            page: newPage,
            per_page: PAGE_SIZE,
            status: statusCodes,
          })

          if (res.code === 0) {
            setPage(res.pagination.page)
            setTotal(res.pagination.total)
            setSnapshots(res.snapshots.map((snap) => new Snapshot(snap)) as T[])
          }
        }
      } catch (err) {
        console.error('Failed to fetch snapshots:', err)
        setError(err as Error)
      } finally {
        setLoading(false)
      }
    },
    [type, teamResID, enabled],
  )

  // Fetch on filter changes - only triggers when serialized keys change
  useEffect(() => {
    if (enabled) {
      fetchSnapshots(1)
    }
  }, [enabled, fetchSnapshots, domainsKey, membersKey, statusesKey])

  return {
    snapshots,
    loading,
    page,
    total,
    error,
    fetchSnapshots,
    setPage,
  }
}
